import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

// COMPONENTS
import CartItemList from 'components/Cart/CartContent/CartItemList/CartItemList';
import CartEmpty from 'components/Cart/CartEmpty/CartEmpty';
import CartSummary from 'components/Cart/CartSummary/CartSummary';
import ModalErrorAlert from 'components/ModalAlert/ModalErrorAlert';
import ToastAlert from 'components/ToastAlert/ToastAlert';
import ToastErrorAlert from 'components/ToastAlert/ToastErrorAlert';

// DS
import {
  VertContainer,
  Col,
  VertSkeleton,
  Row,
  VertTypography,
  VertAlert,
} from '@vertem-core/vertice-ds';

// SERVICES
import PipeProvider from 'Services/Providers/PipeProvider';
import usePipeContext from 'Services/Providers/PipeProvider/usePipeContext';

// STYLES
import 'Assets/styles/default/pages/cart/CartStyles.less';

// UTILS
import generateUUID from 'Utils/generateUUID';
import ynToBool from 'Utils/ynToBool';
import { FETCH_RESET_PURCHASE } from '../../redux/Reducers/Checkout/CheckoutReducer';
import ShowCaseAutomate from 'components/ShowCaseAutomate/ShowCaseAutomate';
import { ShowcaseDisplayAreaEnum } from 'Constants/ShowCaseConstants';
import validateCEP from 'Utils/isValidateCEP';
import { useState } from 'react';

// CART PAGE
const CartPage = () => {
  /* Hooks */
  const { callApi } = usePipeContext();
  const { search } = useLocation();
  const navigate = useNavigate();
  const isLargeScreen = useMediaQuery({ minWidth: 769 });
  const itemsCartReducer = useSelector(({ Cart: data }) => data);
  const createPurchase = useSelector(({ CreatePurchase: data }) => data);
  const ShippingCart = useSelector(({ Shipping: data }) => data);
  const Participant = useSelector(({ Participants: data }) => data);

  const { isAlertErrorVoucher } = useSelector(
    ({ CartReducerTable: data }) => data
  );

  const Dispatch = useDispatch();
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [hasItemUnavailable, setHasItemUnavailable] = useState(false)

  const { error } = ShippingCart

  /* Vars */
  const { cart } = itemsCartReducer;
  const hasProduct = (cart && cart?.items && cart?.items.length > 0) || 0;
  const isLoading =
    ynToBool(itemsCartReducer?.isLoading) || ynToBool(ShippingCart?.isLoading);
  const shippingError =
    ShippingCart && ShippingCart?.error && ShippingCart.error.subs;
  const cartError = itemsCartReducer && itemsCartReducer?.error;
  const hasShippingError = !!shippingError;
  const hasCartError = !!cartError;
  const totalShipping = ShippingCart?.value?.total;
  const hasShippingValue = totalShipping === 0 || totalShipping > 0;
  const replacementCEPIfParticipantHasNoCEP =
    sessionStorage.getItem('replacementCep');
  const ShippingError = ShippingCart?.isError;

  const shippingFail = error?.code == 131 || error?.statusCode == 404

  /* Handlers */
  const goToCheckoutHandler = () => {
    if (hasCartError || ShippingError) {
      return;
    }
    navigate('/checkout');
  };
  
  /* Lifecycles */
  useEffect(() => {
    if (
      Participant &&
      Participant.address &&
      (Participant.address.zipCode || replacementCEPIfParticipantHasNoCEP) &&
      !ShippingCart.isLoading
    ) {
      const aliasProviderUUID = generateUUID();
      callApi({
        symbol: 'FETCH_CART_SHIPPING',
        alias: aliasProviderUUID,
        payload: {
          zipCode:
            replacementCEPIfParticipantHasNoCEP ||
            Participant.address.zipCode.value,
        },
      });
    }
  }, [replacementCEPIfParticipantHasNoCEP, itemsCartReducer?.changeQuantity, ShippingCart?.cep]);

  useEffect(() => {

    if (hasShippingError && Participant.address.zipCode.value) {
      setIsEditingAddress(true)

    }
  }, [hasShippingError]);

  useEffect(() => {
    if (
      itemsCartReducer?.finishRequests &&
      !itemsCartReducer?.hasError &&
      ShippingCart &&
      (replacementCEPIfParticipantHasNoCEP ||
        Participant.address.zipCode.value) &&
      !ShippingCart.isLoading
    ) {
      const aliasProviderUUID = generateUUID();

      if (
        !validateCEP(
          Participant.address.zipCode.value ||
          replacementCEPIfParticipantHasNoCEP
        ) ||
        ShippingError
      ) {
        return;
      }

      callApi({
        symbol: 'FETCH_CART_SHIPPING',
        alias: aliasProviderUUID,
        payload: {
          zipCode:
            (ShippingCart && ShippingCart.cep) ||
            replacementCEPIfParticipantHasNoCEP ||
            Participant.address.zipCode.value,
        },
      });
    }
  }, [
    itemsCartReducer?.finishRequests,
    Participant,
    replacementCEPIfParticipantHasNoCEP,
  ]);

  useEffect(() => {
    if (
      ShippingCart &&
      Participant &&
      Participant.address &&
      Participant.address.zipCode &&
      (replacementCEPIfParticipantHasNoCEP ||
        Participant.address.zipCode.value) &&
      (!ShippingCart.error ||
        (ShippingCart.error && replacementCEPIfParticipantHasNoCEP)) &&
      ((ShippingCart.value &&
        !ShippingCart.isLoading &&
        !ShippingCart.value.code &&
        !ShippingCart.value.city) ||
        (!ShippingCart.value && !ShippingCart.isLoading))
    ) {
      const aliasProviderUUID = generateUUID();

      if (
        !validateCEP(
          Participant.address.zipCode.value ||
            replacementCEPIfParticipantHasNoCEP
        ) ||
        ShippingError
      ) {
        return;
      }
      callApi({
        symbol: `FETCH_ADDRESS_SHIPPING`,
        alias: aliasProviderUUID,
        payload: {
          zipCode:
            replacementCEPIfParticipantHasNoCEP ||
            Participant.address.zipCode.value,
        },
      });
    }
  }, [ShippingCart, Participant, replacementCEPIfParticipantHasNoCEP]);

  /* Renders */
  if (
    !hasProduct ||
    Participant.isLoading ||
    Participant.isLoaded ||
    !ShippingCart.value
  ) {
    return (
      <VertContainer className="cart-page-wrapper" spacingX="regular">
        <CartEmpty />
      </VertContainer>
    );
  }

  return (
    <PipeProvider
      apiSymbols={['FETCH_CART', 'FETCH_PARTICIPANT', 'FETCH_CATALOG']}
      loadingComponent={
        <VertContainer className="cart-page-wrapper" spacingX="regular">
          <Row className="cart-pag">
            <Col className="cart-page-content">
              <VertSkeleton paragraph={{ rows: 4 }} active />
            </Col>
            <VertSkeleton avatar paragraph={{ rows: 4 }} active />
          </Row>
        </VertContainer>
      }
    >
      <VertContainer className="cart-page-wrapper" spacingX="regular">
        <Row align="top" gutter={{ xs: 4, md: 16, lg: 32 }}>
          <Col className="cart-page-content col">
            {(() => {
              if (/(2faerror=true)/g.test(search)) {
                console.log({ createPurchase });
                if (createPurchase && createPurchase.status)
                  Dispatch({ type: FETCH_RESET_PURCHASE });
                return (
                  <>
                    <VertAlert
                      type="error"
                      className="toast-alert"
                      message={
                        'Parece que você atingiu o limite de tentativas.'
                      }
                      description={
                        'Não se preocupe, respire fundo e tente novamente mais tarde.😊'
                      }
                      closable={false}
                    />
                  </>
                );
              } else if (/(2facancel=true)/g.test(search)) {
                if (createPurchase && createPurchase.status)
                  Dispatch({ type: FETCH_RESET_PURCHASE });
                return (
                  <>
                    <VertAlert
                      type="error"
                      className="toast-alert"
                      message={
                        'Desculpe, o código de 2FA não pôde ser validado, impedindo a conclusão do pedido.'
                      }
                      description={
                        'Por favor, verifique e tente novamente. Agradecemos sua compreensão.'
                      }
                      closable={false}
                    />
                  </>
                );
              }
              return null;
            })()}
            <ToastAlert
              className="margin-bottom-small"
              message="Os itens no carrinho não ficam reservados. Finalize seu resgate para garantir os produtos."
              size="small"
            />
            {isAlertErrorVoucher && (
              <VertAlert
                type="error"
                className="toast-alert"
                message=" Por favor, finalize resgates de produtos e vouchers virtuais separadamente."
                description="Você possui produtos e vouchers virtuais no carrinho. Finalize os resgates separadamente."
                closable={false}
              />
            )}

            {/* Error alert and modal */}
            {ShippingError && shippingFail && (
              <VertAlert
                type='error'
                className="toast-alert"
                message="Erro ao calcular frete. CEP inválido."
                description="Por favor, informe um CEP válido para continuar."
                closable={false}
              />
            )}

            {hasItemUnavailable && (
              <VertAlert
                type='error'
                className="toast-alert"
                message="Você possui itens indisponíveis em seu carrinho. Exclua para continuar com o resgate."
                closable={false}
              />
            )}
            {itemsCartReducer?.error && hasCartError && (
              <>
                <ToastErrorAlert
                  className="margin-bottom-small"
                  error={itemsCartReducer?.error}
                />
                <ModalErrorAlert
                  error={itemsCartReducer?.error}
                  hasError={hasCartError}
                />
              </>
            )}


            {!Participant.address.zipCode.value && !hasShippingValue && (
              <>
                <ToastErrorAlert
                  className="margin-bottom-small"
                  error={{
                    error: {
                      message: 'CEP não informado.',
                      subMessage:
                        'Você não possui CEP cadastrado em seu perfil.',
                    },
                  }}
                />
                <ModalErrorAlert
                  error={{
                    message: 'CEP não informado.',
                    subMessage:
                      'Você não possui CEP cadastrado em seu perfil. Por favor, cadastre um CEP válido para continuar ou pesquise um CEP no resumo do resgate.',
                  }}
                  hasError={!Participant.address.zipCode.value}
                />
              </>
            )}

            {/* cart content items */}
            <Col className="cart-content-items">
              <Row className="cart-content-title">
                <Col>
                  <VertTypography.Title>Meu Carrinho</VertTypography.Title>
                </Col>
              </Row>
              <Row className="cart-content-label">
                <Col span={12}>
                  <VertTypography.Paragraph>Itens</VertTypography.Paragraph>
                </Col>
                <Col span={4}>
                  <VertTypography.Paragraph>
                    Valor unitário
                  </VertTypography.Paragraph>
                </Col>
                <Col span={4}>
                  <VertTypography.Paragraph>
                    Quantidade
                  </VertTypography.Paragraph>
                </Col>
                <Col span={4}>
                  <VertTypography.Paragraph>Total</VertTypography.Paragraph>
                </Col>
              </Row>
              <CartItemList
                setHasItemUnavailable={setHasItemUnavailable}
                hasItemUnavailable={hasItemUnavailable}
              />
            </Col>
          </Col>

          {/* cart summary */}
          <Col
            className={`cart-page-content ${isLargeScreen ? 'col-auto' : 'col-12'
              }`}
          >
            <CartSummary
              hideShipping={false}
              buttonText="Ir para pagamento"
              hasItemsVirtualVoucher={itemsCartReducer?.hasItemsVirtualVoucher}
              isLoading={isLoading}
              onClickButton={goToCheckoutHandler}
              hasError={hasItemUnavailable || hasCartError || shippingFail}
              isEditingAddress={isEditingAddress}
              setIsEditingAddress={setIsEditingAddress}
              shippingFail={shippingFail}
            />
          </Col>
          <Col
            className="cart-page-content"
            span={24}
            verticalSpacing="regular"
          ></Col>
        </Row>
      </VertContainer>
      <ShowCaseAutomate
        idDisplayAreaId={ShowcaseDisplayAreaEnum.CART_SHOWCASE_MENU}
      />
    </PipeProvider>
  );
};

export default CartPage;
